import { ICgGroup, ISolutionBundleItem } from '@gn-audio/core/api/compatibility-guide/models';
import { fetchGroupPrices } from '@gn-audio/core/api/pricing/fetch-group-prices';
import { IFromPrice, ISFCCGroupPrice } from '@gn-audio/core/api/pricing/models';
import { IGroup } from '@gn-audio/core/api/product-catalogue/models/group';
import { fetchEcomSetting } from '@gn-audio/core/api/site-settings/fetch-ecom-setting';
import { fetchPriceDisplaySetting } from '@gn-audio/core/api/site-settings/fetch-price-display-setting';
import { IEcomSetting, IPriceDisplaySetting } from "@gn-audio/core/api/site-settings/models";
import { PriceDisplayOption } from "@gn-audio/core/api/site-settings/enums";
import { SegmentType } from '@gn-audio/core/common/enums';
import { Component, Inject, Input, OnChanges, OnInit } from 'angular-ts-decorators';
import { IProductApiConfig } from 'src/Foundation/ProductApi/platform';

import template from './cg-customizer-total.component.html';

@Component({
  selector: 'cgCustomizerTotal',
  template,
})
export class CgCustomizerTotalComponent implements OnInit, OnChanges {
  public static $inject: ReadonlyArray<string> = ['$q', 'productApiConfig'];

  @Input() public group?: IGroup;
  @Input() public bundles?: ISolutionBundleItem[];

  public formattedPrice = '';
  public formattedPriceVat = '';
  public loading: boolean;
  public price?: number;
  public priceVat?: number;

  private displayOption?: PriceDisplayOption;
  private displayThreshold?: number;

  constructor(
    @Inject('$q') private $q: ng.IQService,
    @Inject('productApiConfig') private productApiConfig: IProductApiConfig,
  ) { }

  public ngOnInit(): void {
    this.loading = true;
    this.$q.when(fetchEcomSetting())
      .then((ecomSetting: IEcomSetting | null): void => {
        if (!ecomSetting?.ecomEnabled) {
          this.loading = false;
          return;
        }

        this.$q.when(fetchPriceDisplaySetting())
          .then((setting: IPriceDisplaySetting): void => {
            this.displayOption = setting.priceDisplayOption;
            this.displayThreshold = setting.priceDisplayThreshold;
          })
          .finally(() => {
            this.loading = false;
          });
      });
  }

  public ngOnChanges(): void {
    if (!this.group || isNaN(this.group.groupId)) {
      return;
    }

    const groupIds: number[] = [this.group.groupId];
    (this.bundles || []).forEach(
      (b: ISolutionBundleItem): void => {
        const bundleGroupIds: number[] = b.bundle.map((g: ICgGroup): number => g.groupId);
        Array.prototype.push.apply(groupIds, bundleGroupIds);
      },
    );

    this.loading = true;
    this.$q.when(fetchEcomSetting())
      .then((ecomSetting: IEcomSetting | null): void => {
        if (!ecomSetting?.ecomEnabled) {
          this.loading = false;
          return;
        }

        this.$q.when(fetchGroupPrices(groupIds))
          .then((prices: IFromPrice[] | ISFCCGroupPrice[]) => { this.calculate(prices); })
          .finally((): void => { this.loading = false; });
      });
  }

  public showVat(): boolean {
    if (this.isExcludeIncludeVat() && this.isBusiness()) {
      return true;
    }

    if (this.isIncludesVATLabel() && (this.isBusiness() || this.isConsumer())) {
      return true;
    }

    if (this.isExcludeVATThreshold() && (this.isBusiness() || this.price < this.displayThreshold)) {
      return true;
    }

    return false;
  }

  public hasPrice(): boolean {
    return Boolean(this.getPrice());
  }

  public isExcludeIncludeVat(): boolean {
    return this.displayOption === PriceDisplayOption.ExcludeIncludeVAT;
  }

  public isExcludeVATThreshold(): boolean {
    return this.displayOption === PriceDisplayOption.ExcludeVATThreshold;
  }

  public isIncludesVATLabel(): boolean {
    return this.displayOption === PriceDisplayOption.IncludesVATLabel;
  }

  public getPrice(): string {
    switch (this.displayOption) {
      case PriceDisplayOption.Simple:
        return this.formattedPrice;

      case PriceDisplayOption.ExcludeIncludeVAT:
      case PriceDisplayOption.ExcludeVATThreshold:
        return this.formattedPriceVat;

      default:
        return '';
    }
  }

  public getVatPrice(): string {
    switch (this.displayOption) {
      case PriceDisplayOption.Simple:
        return '';

      case PriceDisplayOption.ExcludeIncludeVAT:
      case PriceDisplayOption.ExcludeVATThreshold:
        return this.formattedPrice;

      default:
        return '';
    }
  }

  private isBusiness(): boolean {
    return this.group?.segmentType === SegmentType.Business;
  }

  private isConsumer(): boolean {
    return this.group?.segmentType === SegmentType.Consumer;
  }

  private calculate(prices: (IFromPrice | ISFCCGroupPrice)[] = []): void {
    this.formattedPrice = '';
    this.formattedPriceVat = '';

    if (prices.length === 0) {
      return;
    }

    this.price = prices
      .filter((p: IFromPrice | ISFCCGroupPrice): boolean => !!p.fromDiscountedPrice || !!p.fromListedPrice)
      .map((p: IFromPrice | ISFCCGroupPrice): number => p.fromDiscountedPrice || p.fromListedPrice)
      .reduce((previous: number, current: number): number => previous + current, 0);

    switch (this.displayOption) {
      case PriceDisplayOption.Simple:
        break;

      case PriceDisplayOption.ExcludeIncludeVAT:
      case PriceDisplayOption.IncludesVATLabel:
      case PriceDisplayOption.ExcludeVATThreshold:
        this.priceVat = prices
          .filter((p: IFromPrice | ISFCCGroupPrice): boolean => !!p.fromDiscountedPriceWithoutVat || !!p.fromListedPriceWithoutVat)
          .map((p: IFromPrice | ISFCCGroupPrice): number => p.fromDiscountedPriceWithoutVat || p.fromListedPriceWithoutVat)
          .reduce((previous: number, current: number): number => previous + current, 0);
        break;

      default:
        break;
    }

    if (this.price > 0) {
      this.formattedPrice = this.price.toLocaleString(this.productApiConfig.language, {
        currency: prices[0].currency,
        style: 'currency',
      });
    }

    if (this.priceVat > 0) {
      this.formattedPriceVat = this.priceVat.toLocaleString(this.productApiConfig.language, {
        currency: prices[0].currency,
        style: 'currency',
      });
    }
  }
}
